import { TIER_BONUSES, TIER_BONUSES_CREDITED } from "../../config/supernovaXp";

/**
 * supernovaTiers — the reward-tier track, derived. No React, no I/O.
 *
 * THE TIERS ARE CONFIG, NOT COPY. 3 / 5 / 8 / 10 staked → 250k / 500k / 750k /
 * 1M XP lives in config/supernovaXp.TIER_BONUSES; this module only decides which
 * of them a staked count has reached. Nothing here re-types a threshold or a
 * bonus, so the track cannot drift from the model.
 *
 * DISPLAY ONLY — NOTHING IS AWARDED HERE.
 * TODO(backend, supervised): tier awards are the staking engine's to credit, and
 * two inputs for them are still undecided (SUPERNOVA_ENGINE_DESIGN.md OQ-4): the
 * TOKEN-VALUATION MINIMUM that a tier may additionally require, and whether the
 * award is one-shot or re-earnable. This app therefore renders progress against
 * the staked-count requirement and states the valuation caveat on screen. It
 * never adds a bonus to any balance (TIER_BONUSES_CREDITED is false) and no code
 * path here may start doing so.
 *
 * AN UNKNOWN STAKED COUNT IS NOT ZERO. With the stake read unconnected, every
 * `reached` comes back null rather than false — "you haven't reached Tier 1" is a
 * claim we cannot make about someone whose stakes we can't see.
 *
 * COMPLIANCE: the bonuses are in-app points with no monetary value. Nothing in
 * this module is a return, a yield, income, a price, or a projection.
 */

export const TIER_VALUATION_NOTE =
  "A minimum token valuation may also be required for a tier award. That " +
  "minimum isn't decided yet, so this track shows the staked-count requirement " +
  "only.";

export const TIER_DISPLAY_ONLY_NOTE =
  "Display only — tier bonuses are the staking engine's to award and are not " +
  "credited to any balance by this app.";

export const TIER_UNKNOWN_NOTE =
  "We can't show your tier progress yet: your staked count isn't connected " +
  "here. The tiers themselves are shown so you can see what they are.";

const isCount = (n) => typeof n === "number" && Number.isFinite(n) && n >= 0;

/**
 * @typedef {Object} TierStep
 * @property {string} key
 * @property {number} index          1–4
 * @property {string} name           "Tier 1"
 * @property {number} requiredStaked NFTs staked to reach it
 * @property {number} bonusXp        the lump XP bonus (NOT credited here)
 * @property {boolean|null} reached  null ⇒ the staked count is unknown
 * @property {number|null} remaining stakes still needed; null ⇒ unknown
 */

/**
 * @param {number|null} stakedCount  staked Supernovas; null ⇒ unknown
 * @returns {{
 *   known: boolean,
 *   stakedCount: number|null,
 *   tiers: TierStep[],
 *   currentTier: TierStep|null,
 *   currentLabel: string|null,
 *   nextTier: TierStep|null,
 *   stakesToNext: number|null,
 *   topReached: boolean,
 *   credited: boolean,
 * }}
 */
export function buildTierTrack(stakedCount = null) {
  const known = isCount(stakedCount);

  const tiers = TIER_BONUSES.map((tier, i) => ({
    key: `supernova-tier-${i + 1}`,
    index: i + 1,
    name: `Tier ${i + 1}`,
    requiredStaked: tier.stakedCount,
    bonusXp: tier.bonusXp,
    reached: known ? stakedCount >= tier.stakedCount : null,
    remaining: known ? Math.max(0, tier.stakedCount - stakedCount) : null,
  }));

  const reached = known ? tiers.filter((t) => t.reached) : [];
  const currentTier = reached.length ? reached[reached.length - 1] : null;
  const nextTier = known ? tiers.find((t) => !t.reached) || null : null;

  return {
    known,
    stakedCount: known ? stakedCount : null,
    tiers,
    currentTier,
    currentLabel: known
      ? currentTier
        ? `${currentTier.name} reached`
        : `Below ${tiers[0].name}`
      : null,
    nextTier,
    stakesToNext: nextTier ? nextTier.remaining : null,
    topReached: known && !nextTier,
    /* Always false in this build. Kept as a field so a component can't decide on
       its own to present a bonus as credited. */
    credited: TIER_BONUSES_CREDITED,
  };
}

export default buildTierTrack;
